import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import * as styles from "./floor-input-info.module.scss";
import PinnedList from "../pinned-list";
import PinnedListHeader from "../pinned-list-header";
import VelavuSelect from "../../../../elements/velavu-select";
import {
	getAvailableFloors,
	getAvailableSiteFloors,
	getDefaultFloor,
} from "../../../../helper/floor-helper";
import {
	getDefaultFloorName,
	mapFloorNumber,
} from "../../../../helper/language-helper";
import VelavuInput from "../../../../elements/velavu-input";
import { VelavuFloor } from "velavu-js-api";
import VelavuNotice from "../../../../elements/velavu-notice/velavu-notice";
import { makeStyleable } from "../../../../helper/icon-helper";
import IconError from "../../../../dynamicicons/icon-error";

export interface FloorInputDetailsProps {
	existingFloors: VelavuFloor[];

	level: number;
	onChangeLevel?: (level: number) => void;
	name: string;
	onChangeName?: (name: string) => void;
	onSave?: () => void;
}

/**
 * An input component that allows the user to specify basic information
 * about a floor, such as its floor number and name.
 */
export function FloorInputInfo(props: FloorInputDetailsProps) {
	const { t } = useTranslation();

	const selectionMatchesExistingFloor = useMemo(() => {
		return props.existingFloors.some(
			(floor) => floor.level === props.level,
		);
	}, [props.level, props.existingFloors]);

	return (
		<div className={styles.content}>
			<div className={styles.inputBar}>
				<VelavuSelect
					className={styles.inputFloorNum}
					titleText={t("dashboard.floor_input.floor")}
					value={props.level}
					onChange={props.onChangeLevel}
					fullWidth
				>
					{getAvailableFloors().map((floorNum) => (
						<option key={floorNum} value={floorNum}>
							{mapFloorNumber(floorNum, t)}
						</option>
					))}
				</VelavuSelect>

				<VelavuInput
					className={styles.inputFloorName}
					titleText={t("dashboard.floor_input.name")}
					placeholder={getDefaultFloorName(props.level, t)}
					value={props.name}
					onChange={props.onChangeName}
					fullWidth
				/>
			</div>

			{selectionMatchesExistingFloor && (
				<VelavuNotice icon={makeStyleable(IconError)} type="warning">
					{t("dashboard.floor_input.duplicate_floor_warning")}
				</VelavuNotice>
			)}
		</div>
	);
}
